/** Month-by-month heatmap of the whole archive, with per-year detail. */
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { PageHeader } from '../App'
import { useApi, qs } from '../lib/api'
import { MonthHeatmap, TrendChart } from '../components/charts'
import { YearSelect } from '../components/Select'
import {
  Card, Empty, QueryState, Loading, fmtInt, PartnerLabel,
  AlumniSwitch,
} from '../components/ui'

export default function TimelineReport() {
  const [withAlumni, setWithAlumni] = useState(false)
  const activity = useApi(
    ['activity', withAlumni],
    `stats/activity${withAlumni ? '?include_alumni=1' : ''}`
  )
  const [year, setYear] = useState(null)

  // The year drill-down reads the same switch as the grid above it, or picking a year
  // would list a different set of updates than the month cells just counted.
  const detail = useApi(
    ['timeline-year', year, withAlumni],
    `newsletters${qs({
      year,
      include_alumni: withAlumni ? 1 : null,
      limit: 200, sort: 'published_on', dir: 'asc',
    })}`,
    { enabled: !!year }
  )

  return (
    <PageHeader
      title="Timeline"
      sub="Every month of the archive at a glance — darker means more updates"
      actions={<AlumniSwitch checked={withAlumni} onChange={setWithAlumni} where="updates" />}
    >
      <QueryState query={activity}>
        {(d) => {
          const months = d.byMonth
          const gaps = months.length
            ? monthsWithNothing(months)
            : []

          return (
            <div className="col">
              {/* No stat tiles: months covered, the busiest one, the empty ones and the
                  span were all four of them readings of the grid below, which shows
                  each of those directly — and the page opens on it now. The empty
                  months still get their own card, since a gap is worth naming. */}
              <Card
                title="Monthly Heatmap"
                sub="Rows are years, columns are months — click a month to read its updates"
              >
                <MonthHeatmap
                  byMonth={months}
                  hrefForMonth={(month) => monthHref(month, withAlumni)}
                />
              </Card>

              <Card title="Monthly Volume">
                <TrendChart data={months} x="month" series={[{ key: 'n', label: 'Updates' }]} area height={260} />
              </Card>

              {gaps.length ? (
                <Card title="Months with No Updates" sub="Inside the covered range">
                  <div className="chip-row">
                    {gaps.map((m) => (
                      <span key={m} className="badge">
                        {m}
                      </span>
                    ))}
                  </div>
                </Card>
              ) : null}

              <Card
                title="Drill into a Year"
                actions={
                  <div style={{ width: 180 }}>
                    <YearSelect value={year} onChange={setYear} placeholder="Pick a year…" />
                  </div>
                }
              >
                {!year && <Empty icon="clock" title="Choose a year to list its updates in order" />}
                {year && detail.isLoading && <Loading />}
                {year && detail.data && (
                  <>
                    <p className="small muted">
                      {fmtInt(detail.data.total)} updates in {year}
                      {detail.data.total > detail.data.rows.length &&
                        ` — showing the first ${detail.data.rows.length}`}
                    </p>
                    <div className="table-wrap flush">
                      <table className="data compact">
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th>Partner Unit</th>
                            <th>Country</th>
                            <th>Text</th>
                          </tr>
                        </thead>
                        <tbody>
                          {detail.data.rows.map((r) => (
                            <tr key={r.id}>
                              <td className="nowrap">
                                <Link className="mono" to={`/newsletters/${r.id}`}>
                                  {r.published_on}
                                </Link>
                              </td>
                              <td>
                                <PartnerLabel id={r.partner_id} code={r.s4ca_code} name={r.partner_name} />
                              </td>
                              <td className="small">{r.country_name}</td>
                              <td className="cell-snippet-cell">
                                <div className="cell-snippet">{r.snippet}</div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </Card>
            </div>
          )
        }}
      </QueryState>
    </PageHeader>
  )
}

/**
 * A month on the heatmap opens the newsletter list narrowed to it.
 *
 * `from`/`to` rather than a new `month` param: the list already reads both out of
 * the URL and shows them in its date-range field, so the arriving filter is visible
 * and can be widened in place. `Date.UTC(y, m, 0)` is the last day of month `m`
 * (day 0 of the next one), which gets February and the leap years right.
 *
 * Both sides leave alumni out by default, so the count in the tooltip is the count
 * that comes up — and when the switch on this page has them shown, the link carries
 * that through so the list it opens still agrees with the cell that was clicked.
 */
function monthHref(month, withAlumni) {
  const [y, m] = month.split('-').map(Number)
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate()
  return `/newsletters${qs({
    from: `${month}-01`,
    to: `${month}-${last}`,
    include_alumni: withAlumni ? 1 : null,
  })}`
}

/** Months between the first and last recorded month that have no newsletters. */
function monthsWithNothing(months) {
  const present = new Set(months.map((m) => m.month))
  const [startY, startM] = months[0].month.split('-').map(Number)
  const [endY, endM] = months[months.length - 1].month.split('-').map(Number)
  const out = []
  let y = startY
  let m = startM
  while (y < endY || (y === endY && m <= endM)) {
    const key = `${y}-${String(m).padStart(2, '0')}`
    if (!present.has(key)) out.push(key)
    m += 1
    if (m > 12) {
      m = 1
      y += 1
    }
  }
  return out
}
